import React, { useEffect, useMemo, useState } from "react";

import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import debounce from "lodash.debounce";
import { useTranslation } from "react-i18next";

import MediaAPI from "@/src/api/MediaAPI";
import { ComboboxBase } from "@/src/components/form-fields/Combobox";

import type { JSX } from "react";

import type { ComboboxOption } from "@/src/components/form-fields/MultiCombobox";

export type TrailerOption = ComboboxOption;

interface TrailerSelectProps {
  trailerId: string | null;
  onChange: (option: TrailerOption | null) => void;
  isDisabled?: boolean;
}

function TrailerSelect({
  trailerId,
  onChange,
  isDisabled,
}: TrailerSelectProps): JSX.Element {
  const { t } = useTranslation("editor-content");
  const [search, setSearch] = useState("");
  const [selectedOption, setSelectedOption] = useState<TrailerOption | null>(
    null,
  );

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useInfiniteQuery({
      queryKey: ["getFreeMediaList", { search }],
      queryFn: async ({ pageParam = 1 }) =>
        MediaAPI.getFreeMediaList({
          page: pageParam,
          search,
        }),
      getNextPageParam: (lastPage, allPages) =>
        allPages.length < lastPage.page.total ? allPages.length + 1 : undefined,
    });

  const options: TrailerOption[] = useMemo(
    () =>
      data?.pages
        .flatMap((page) => page.data)
        .map((media) => ({ value: media.uuid, label: media.label })) ?? [],
    [data],
  );

  const hasLocalLabel = selectedOption?.value === trailerId;

  const getMediaQuery = MediaAPI.generateQuery({
    fn: MediaAPI.getMedia,
    params: [trailerId ?? ""],
  });
  const { data: selectedMediaTrailer } = useQuery({
    ...getMediaQuery,
    enabled: Boolean(trailerId) && !hasLocalLabel,
  });

  const resolvedLabel = hasLocalLabel
    ? selectedOption?.label
    : selectedMediaTrailer?.label;
  const selectedLabel = trailerId && resolvedLabel ? resolvedLabel : null;

  const debouncedSetSearch = useMemo(
    () => debounce((value: string) => setSearch(value), 500),
    [],
  );

  useEffect(() => () => debouncedSetSearch.cancel(), [debouncedSetSearch]);

  const handleSelect = (value: string): void => {
    const option = options.find(
      (currentOption) => currentOption.value === value,
    );
    if (!option) {
      return;
    }
    onChange(option);
    setSelectedOption(option);
  };

  const handleClear = (): void => {
    onChange(null);
    setSelectedOption(null);
  };

  const handleLoadMore = (): void => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage().catch(() => undefined);
    }
  };

  return (
    <ComboboxBase
      className="max-w-75"
      clearLabel={t("clearSelection")}
      disabled={isDisabled}
      emptyLabel={t("no_results", { ns: "common" })}
      isFetchingMore={isFetchingNextPage}
      isLoading={isLoading}
      loadingLabel={t("loading")}
      onClear={handleClear}
      onLoadMore={handleLoadMore}
      onSearchChange={debouncedSetSearch}
      onValueChange={handleSelect}
      options={options}
      placeholder={t("select")}
      searchPlaceholder={t("search")}
      selectedLabel={selectedLabel}
      value={trailerId}
    />
  );
}

export default TrailerSelect;
